/**
 * Wait for a store getter to have a specific value
 * @param {Object} store - Vuex store instance
 * @param {string} getterPath - Full getter path (e.g., 'BusinessStore/featureFlagsLoaded')
 * @param {number} [timeout=10000] - Maximum time to wait (in milliseconds) before throwing error
 * @param {*} [expectedValue=true] - The specific value to wait for
 * @returns {Promise} Promise that resolves when getter has the expected value, or rejects if timeout is reached
 * 
 * @example
 * // Wait for feature flags to be loaded (waits for true)
 * await waitForStoreGetter(store, 'BusinessStore/featureFlagsLoaded');
 * 
 * // Wait for a specific string value
 * await waitForStoreGetter(store, 'SomeStore/status', 10000, 'completed');
 * 
 * // Wait for a specific number
 * await waitForStoreGetter(store, 'SomeStore/count', 5000, 5);
 * 
 * // Wait for false specifically
 * await waitForStoreGetter(store, 'SomeStore/isLoading', 10000, false);
 */
const waitForStoreGetter = (store, getterPath, timeout = 10000, expectedValue = true) => new Promise((resolve, reject) => {
  // Check if value already matches expected value
  const currentValue = store.getters[getterPath];
  if (currentValue === expectedValue) {
    resolve(currentValue);
    return;
  }

  let unwatch;
    
  const timeoutId = setTimeout(() => {
    if (unwatch) {
      unwatch();
    }
    reject(new Error(`Store getter timeout: ${getterPath} (expected: ${expectedValue})`));
  }, timeout);

  // Use store.watch for better performance - only fires when this specific getter changes
  unwatch = store.watch(
    () => store.getters[getterPath],
    (newValue) => {
      if (newValue === expectedValue) {
        clearTimeout(timeoutId);
        unwatch();
        resolve(newValue);
      }
    },
  );
});

export default waitForStoreGetter; 
